import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useMenu } from "../../../context/MenuContext";
import ProductGridSkeleton from "../../../skeletons/ProductGridSkeleton";
import ChildCategoryProducts from "./ChildCategoryProducts";
import Noproduct from "../../../../assets/common/no-product.webp";
import CategoryBannerSlider from "./CategoryBannerSlider";
import FilterSidebar from "./FilterSidebar";
import { CommonFilter } from "./CommonFilter";
import Swal from "sweetalert2";
import Slider from "rc-slider";
import "rc-slider/assets/index.css";

const colorClass = {
  red: "bg-red-500",
  blue: "bg-blue-500",
  green: "bg-green-500",
  black: "bg-black",
  white: "bg-white border",
  yellow: "bg-yellow-400",
  gray: "bg-gray-500",
  "royal blue": "bg-blue-700",
};

const Men = () => {
  const [productsData, setProductsData] = useState([]);
  const [hoverImages, setHoverImages] = useState({});
  const [relatedHoverImages, setRelatedHoverImages] = useState({});
  const [sortBy, setSortBy] = useState("Latest");
  const [loading, setLoading] = useState(true);
  const [popupImage, setPopupImage] = useState(null);
  const [selectedColors, setSelectedColors] = useState([]);
  const [selectedSizes, setSelectedSizes] = useState([]);
  const [stockFilter, setStockFilter] = useState("all");
  const [openMain, setOpenMain] = useState(null);
  const [openSub, setOpenSub] = useState(null);
  const [selectedPrice, setSelectedPrice] = useState(null);
  const [bannerData, setBannerData] = useState([]);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [showAllColors, setShowAllColors] = useState(false);
  const [showAllSizes, setShowAllSizes] = useState(false);
  const [priceRange, setPriceRange] = useState([0, 0]);
  const [selectedMainId, setSelectedMainId] = useState(null);
  const [selectedChildId, setSelectedChildId] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const navigate = useNavigate();
  const { slug } = useParams();

  // ✅ get context values
  const {
    menuItems,
    selectedMain,
    selectedSub,
    selectedChild,
    selectMain,
    selectSub,
    selectChild,
    fetchMenuCounts,
    setCartCount,
    setWishlistCount,
  } = useMenu();

  // 🔹 Fetch products by slug
  useEffect(() => {
    setLoading(true);
    fetch(
      `https://tyka.premierhostings.com/beta/api/child-category/${slug}/products`,
    )
      .then((res) => res.json())
      .then((data) => {
        const products = data?.data?.products || [];
        const banners = data?.data?.banners || [];

        setProductsData(products);
        const bannerData = [
          ...banners,
          ...(data?.data?.video
            ? [
                {
                  id: Date.now(),
                  banner_type: "video",
                  image: data.data.video, // video URL
                },
              ]
            : []),
        ];
        console.log("bannerData", bannerData);

        setBannerData(bannerData);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching products:", err);
        setLoading(false);
      });
  }, [slug]);

  const currentCategory = menuItems.find((c) => c.slug === slug);

  const childCategories =
    currentCategory?.subcategories.flatMap((sub) => sub.children) || [];

  // 🔹 Sort products
  const sortProducts = (products) => {
    if (!Array.isArray(products)) return [];
    switch (sortBy) {
      case "Older":
        return [...products].reverse();
      default:
        return products;
    }
  };

  // 🔹 Color Hover Handling
  const handleColorHover = (productId, image) => {
    if (image) setHoverImages((prev) => ({ ...prev, [productId]: image }));
  };

  const handleColorLeave = (productId) => {
    setHoverImages((prev) => {
      const updated = { ...prev };
      delete updated[productId];
      return updated;
    });
  };

  const handleRelatedColorHover = (productId, image) => {
    if (image)
      setRelatedHoverImages((prev) => ({ ...prev, [productId]: image }));
  };

  const handleRelatedColorLeave = (productId) => {
    setRelatedHoverImages((prev) => {
      const updated = { ...prev };
      delete updated[productId];
      return updated;
    });
  };

  // 🔹 Add to Cart
  const handleAddToCart = async (e, product) => {
    e.stopPropagation();
    if (!product?.id) return;

    const payload = {
      product_id: product.id,
      color_id: product?.variants?.[0]?.color_id || null,
      size_id: product?.variants?.[0]?.size_id || null,
      quantity: 1,
    };

    try {
      const authToken = localStorage.getItem("authToken");
      let guestToken = localStorage.getItem("guest_token");
      let url = `https://tyka.premierhostings.com/beta/api/cart`;
      let headers = { "Content-Type": "application/json" };
      let id = localStorage.getItem("authUserId");
      if (authToken) {
        headers.Authorization = `Bearer ${authToken}`;
      } else if (guestToken) {
        url += `?guest_token=${guestToken}`;
      }

      const res = await fetch(url, {
        method: "POST",
        headers,
        body: JSON.stringify(payload),
      });
      const data = await res.json();

      if (data.success) {
        if (!authToken && data.guest_token) {
          localStorage.setItem("guest_token", data.guest_token);
        }

        if (!authToken) {
          setCartCount((prev) => prev + 1); // ✅ Guest update instantly
        } else {
          fetchMenuCounts(id, authToken);
          // fetchMenuCounts(); // ✅ Logged in update via API
        }

        Swal.fire("✅ Added to Cart!", "", "success");
      } else {
        Swal.fire("Failed!", data.message || "Failed to add to cart", "error");
      }
    } catch (err) {
      console.error(err);
      Swal.fire("Error!", "Something went wrong!", "error");
    }
  };

  // 🔹 Add to Wishlist (Guest + Logged-in)
  const handleAddToWishlist = async (e, product) => {
    e.stopPropagation();
    if (!product?.id) return;

    const payload = {
      product_id: product.id,
      color_id: product?.variants?.[0]?.color_id || null,
      size_id: product?.variants?.[0]?.size_id || null,
      quantity: 1,
    };

    try {
      const authToken = localStorage.getItem("authToken");
      let guestToken = localStorage.getItem("guest_token");
      let id = localStorage.getItem("authUserId");

      let url = "https://tyka.premierhostings.com/beta/api/wishlists";
      let headers = { "Content-Type": "application/json" };

      if (authToken) {
        headers.Authorization = `Bearer ${authToken}`;
      } else if (guestToken) {
        url += `?guest_token=${guestToken}`;
      }

      const res = await fetch(url, {
        method: "POST",
        headers,
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (data.success) {
        if (!authToken && data.guest_token) {
          localStorage.setItem("guest_token", data.guest_token);
        }

        if (!authToken) {
          setWishlistCount((prev) => prev + 1); // ✅ Guest update instantly
        } else {
          fetchMenuCounts(id, authToken);
        }

        Swal.fire("💖 Added to Wishlist!", "", "success");
      } else {
        Swal.fire(
          "Failed!",
          data.message || "Failed to add to wishlist",
          "error",
        );
      }
    } catch (err) {
      console.error(err);
      Swal.fire("Error!", "Something went wrong!", "error");
    }
  };

  // 🔹 Color bar renderer
  const colorBar = (product, isRelated = false) => {
    const uniqueColors = product.variants
      ? [...new Map(product.variants.map((v) => [v.color?.id, v])).values()]
      : [];

    return (
      <div className="flex h-[4px] w-full">
        {uniqueColors.map((variant, index) => {
          const color = variant.color;
          const colorHex = color?.hexa_code;
          const colorName = color?.name?.toLowerCase();
          const variantImage = variant.variant_default_image;

          return (
            <div
              key={index}
              className={`w-full h-full cursor-pointer ${
                !colorHex ? colorClass[colorName] || "bg-gray-400" : ""
              }`}
              style={colorHex ? { backgroundColor: colorHex } : {}}
              onMouseEnter={() =>
                isRelated
                  ? handleRelatedColorHover(product.id, variantImage)
                  : handleColorHover(product.id, variantImage)
              }
              onMouseLeave={() =>
                isRelated
                  ? handleRelatedColorLeave(product.id)
                  : handleColorLeave(product.id)
              }
            />
          );
        })}
      </div>
    );
  };

  const allVariants = productsData.flatMap((product) =>
    (product.variants || []).map((variant) => ({
      ...variant,
      productId: product.id,
    })),
  );

  // Colors with product count
  const filterColors = [
    ...new Map(
      allVariants
        .filter((v) => v.color)
        .map((v) => [
          v.color.id,
          {
            ...v.color,
            count: new Set(
              allVariants
                .filter((item) => item.color_id === v.color.id)
                .map((item) => item.productId),
            ).size,
          },
        ]),
    ).values(),
  ];

  // Sizes with product count
  const filterSizes = [
    ...new Map(
      allVariants
        .filter((v) => v.size)
        .map((v) => [
          v.size.id,
          {
            ...v.size,
            count: new Set(
              allVariants
                .filter((item) => item.size_id === v.size.id)
                .map((item) => item.productId),
            ).size,
          },
        ]),
    ).values(),
  ];
  const toggleColor = (id) => {
    const updated = selectedColors.includes(id)
      ? selectedColors.filter((x) => x !== id)
      : [...selectedColors, id];

    setSelectedColors(updated);

    const params = new URLSearchParams(searchParams);

    const names = filterColors
      .filter((c) => updated.includes(c.id))
      .map((c) => c.name);

    if (names.length) {
      params.set("filter_color", names.join(","));
    } else {
      params.delete("filter_color");
    }

    setSearchParams(params);
  };

  const toggleSize = (id) => {
    const updated = selectedSizes.includes(id)
      ? selectedSizes.filter((x) => x !== id)
      : [...selectedSizes, id];

    setSelectedSizes(updated);

    const params = new URLSearchParams(searchParams);

    const names = filterSizes
      .filter((s) => updated.includes(s.id))
      .map((s) => s.name);

    if (names.length) {
      params.set("filter_size", names.join(","));
    } else {
      params.delete("filter_size");
    }

    setSearchParams(params);
  };

  const filteredProducts = productsData.filter((product) => {
    const matchesVariant = product.variants.some((variant) => {
      const colorMatch =
        selectedColors.length === 0 ||
        selectedColors.includes(variant.color_id);

      const sizeMatch =
        selectedSizes.length === 0 || selectedSizes.includes(variant.size_id);

      const stockMatch =
        stockFilter === "all" ||
        (stockFilter === "in" && variant.stock > 0) ||
        (stockFilter === "out" && variant.stock <= 0);

      return colorMatch && sizeMatch && stockMatch;
    });
    const price = Number(product.selling_price || product.price || 0);

    const priceMatch = price >= priceRange[0] && price <= priceRange[1];

    return matchesVariant && priceMatch;
  });

  const visibleColors = showAllColors
    ? filterColors
    : filterColors.slice(0, 10);
  const visibleSizes = showAllSizes ? filterSizes : filterSizes.slice(0, 10);

  const prices = React.useMemo(() => {
    return productsData
      .map((product) => Number(product.selling_price || product.price || 0))
      .filter((price) => price > 0);
  }, [productsData]);

  const minPrice = prices.length ? Math.min(...prices) : 0;
  const maxPrice = prices.length ? Math.max(...prices) : 0;

  useEffect(() => {
    if (prices.length) {
      setPriceRange([minPrice, maxPrice]);
    } else {
      setPriceRange([0, 0]);
    }
  }, [slug, minPrice, maxPrice]);
  useEffect(() => {
    if (!menuItems.length || !slug) return;

    // Reset
    setSelectedMainId(null);
    setSelectedChildId(null);

    // Check Main Category URL
    const mainCategory = menuItems.find((main) => main.slug === slug);

    if (mainCategory) {
      setSelectedMainId(mainCategory.id);
      setOpenMain(mainCategory.id);
      setOpenSub(null);
      return;
    }

    // Check Child Category URL
    for (const main of menuItems) {
      for (const sub of main.subcategories) {
        const child = sub.children.find((c) => c.slug === slug);

        if (child) {
          setSelectedMainId(main.id);
          setSelectedChildId(child.id);

          setOpenMain(main.id);
          setOpenSub(sub.id); // expand only

          return;
        }
      }
    }
  }, [menuItems, slug]);

  useEffect(() => {
    const colorSlugs =
      searchParams.get("filter_color")?.split(",").filter(Boolean) || [];

    const sizeSlugs =
      searchParams.get("filter_size")?.split(",").filter(Boolean) || [];

    const colorIds = filterColors
      .filter((c) => colorSlugs.includes(c.name))
      .map((c) => c.id);

    const sizeIds = filterSizes
      .filter((s) => sizeSlugs.includes(s.name))
      .map((s) => s.id);

    setSelectedColors(colorIds);
    setSelectedSizes(sizeIds);
  }, [searchParams, productsData]);
  const displayedProducts = sortProducts(filteredProducts);

  if (loading) return <ProductGridSkeleton />;

  return (
    <>
      <div className="max-w-7xl mx-auto p-4 lg:p-6 flex gap-8">
        <CommonFilter
          menuItems={menuItems}
          navigate={navigate}
          setStockFilter={setStockFilter}
          stockFilter={stockFilter}
          filterColors={filterColors}
          selectedColors={selectedColors}
          toggleColor={toggleColor}
          filterSizes={filterSizes}
          selectedSizes={selectedSizes}
          toggleSize={toggleSize}
          selectedPrice={selectedPrice}
          setSelectedPrice={setSelectedPrice}
          setOpenMain={setOpenMain}
          openMain={openMain}
          openSub={openSub}
          setOpenSub={setOpenSub}
          selectedMainId={selectedMainId}
          setSelectedMainId={setSelectedMainId}
          slug={slug}
          priceRange={priceRange}
          setPriceRange={setPriceRange}
          minPrice={minPrice}
          maxPrice={maxPrice}
          visibleColors={visibleColors}
          setShowAllColors={setShowAllColors}
          showAllColors={showAllColors}
          visibleSizes={visibleSizes}
          setShowAllSizes={setShowAllSizes}
          showAllSizes={showAllSizes}
          closeSidebar={() => setIsFilterOpen(false)}
        />
        {/* Mobile */}
        {isFilterOpen && (
          <div
            className="fixed inset-0 z-50 bg-black/50 lg:hidden"
            onClick={() => setIsFilterOpen(false)}
          >
            <div
              className="absolute left-0 top-0 h-full w-80 bg-white overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <FilterSidebar
                menuItems={menuItems}
                navigate={navigate}
                setStockFilter={setStockFilter}
                stockFilter={stockFilter}
                filterColors={filterColors}
                selectedColors={selectedColors}
                toggleColor={toggleColor}
                filterSizes={filterSizes}
                selectedSizes={selectedSizes}
                toggleSize={toggleSize}
                selectedPrice={selectedPrice}
                setSelectedPrice={setSelectedPrice}
                setOpenMain={setOpenMain}
                openMain={openMain}
                openSub={openSub}
                setOpenSub={setOpenSub}
                selectedMainId={selectedMainId}
                setSelectedMainId={setSelectedMainId}
                slug={slug}
                priceRange={priceRange}
                setPriceRange={setPriceRange}
                minPrice={minPrice}
                maxPrice={maxPrice}
                visibleColors={visibleColors}
                setShowAllColors={setShowAllColors}
                showAllColors={showAllColors}
                visibleSizes={visibleSizes}
                setShowAllSizes={setShowAllSizes}
                showAllSizes={showAllSizes}
                closeSidebar={() => setIsFilterOpen(false)}
              />
            </div>
          </div>
        )}
        {/* Header: Count + Sorting */}
        <div className="flex-1">
          {bannerData.length > 0 && (
            <CategoryBannerSlider banners={bannerData} />
          )}
          <div className="flex items-center justify-between mb-4 gap-3 px-2">
            {/* Desktop */}
            <h2 className="hidden lg:block text-2xl font-semibold">
              Showing {displayedProducts.length} Products
            </h2>

            {/* Mobile */}
            <button
              onClick={() => setIsFilterOpen(true)}
              className="lg:hidden px-4 py-2 bg-red-600 text-white rounded-md whitespace-nowrap"
            >
              Filter
            </button>

            {/* Sort */}
            <div className="relative flex-1 lg:flex-none">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="appearance-none text-sm text-gray-700 border border-gray-300 rounded px-3 py-2 pr-10 w-full lg:w-auto cursor-pointer focus:outline-none"
              >
                <option value="Latest">Sort by Latest</option>
                <option value="Older">Sort by Older</option>
              </select>

              <span className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-500">
                ⏷
              </span>
            </div>
          </div>

          {/* Product Grid */}
          {displayedProducts.length > 0 ? (
            <ChildCategoryProducts name={slug} data={displayedProducts} />
          ) : (
            <div className="flex min-h-[100vh] items-center rounded-xl border mb-5 border-gray-300 justify-center px-4 py-2">
              <div className="text-center">
                <img
                  src={Noproduct} // or ProductNotFound image
                  alt="Product Not Found"
                  className="w-full max-w-md mx-auto"
                />

                <p className="text-gray-500 mt-2 text-center">
                  No products match your selected filters.
                  <br />
                  Try changing or clearing the filters.
                </p>
                <div className="gap-2 flex flex-col sm:flex-row">
                  <button
                    onClick={() => navigate("/")}
                    className="mt-6 rounded-lg bg-[#FF0009] px-6 py-3 text-white transition hover:bg-red-500"
                  >
                    Continue Shopping
                  </button>
                  <button
                    onClick={() => {
                      setSelectedColors([]);
                      setSelectedSizes([]);
                      setStockFilter("all");
                      setSelectedPrice(null);
                    }}
                    className="mt-6 px-6 py-2 bg-[#FF0009] text-white rounded-md hover:bg-red-600 transition"
                  >
                    Clear Filters
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Men;
